import { forwardRef } from "react";
import { cn } from "@/lib/cn";

/**
 * Slider — Tahoe geometry (approved 2026-06-10).
 *
 * Native <input type="range"> styled via Tailwind arbitrary variants.
 *
 * Track: 5px height, input-fill, rounded-pill. Not a glass veil: desktop Light
 * zeroes every glass fill/stroke (the native plate paints them), so a glass
 * track vanishes there; input-fill is non-zero in every theme.
 * Thumb: 20px white circle (bg-white + shadow-btn-secondary).
 *   NOT accent — accent is reserved for ON-states/primary buttons.
 *
 * Disabled: 40% opacity, pointer-events-none.
 */

type SliderProps = Omit<
  React.ComponentProps<"input">,
  "type" | "onChange" | "value"
> & {
  value: number;
  onValueChange: (value: number) => void;
};

const Slider = forwardRef<HTMLInputElement, SliderProps>(
  ({ value, onValueChange, disabled, className, ...props }, ref) => {
    return (
      <input
        ref={ref}
        type="range"
        value={value}
        disabled={disabled}
        onChange={(e) => onValueChange(Number(e.target.value))}
        className={cn(
          // Base layout
          "w-full cursor-pointer appearance-none bg-transparent",
          "disabled:pointer-events-none disabled:opacity-40",
          // WebKit track
          "[&::-webkit-slider-runnable-track]:h-[5px]",
          "[&::-webkit-slider-runnable-track]:rounded-pill",
          "[&::-webkit-slider-runnable-track]:bg-input-fill",
          // WebKit thumb — bg-white intentional: macOS slider thumbs are always
          // white; --color-readable-overlay-primary goes near-black in light mode.
          "[&::-webkit-slider-thumb]:appearance-none",
          "[&::-webkit-slider-thumb]:mt-[-7.5px]",
          "[&::-webkit-slider-thumb]:h-[20px]",
          "[&::-webkit-slider-thumb]:w-[20px]",
          "[&::-webkit-slider-thumb]:rounded-full",
          "[&::-webkit-slider-thumb]:bg-white",
          "[&::-webkit-slider-thumb]:shadow-btn-secondary",
          // Mozilla track
          "[&::-moz-range-track]:h-[5px]",
          "[&::-moz-range-track]:rounded-pill",
          "[&::-moz-range-track]:bg-input-fill",
          // Mozilla thumb — bg-white intentional: same as WebKit thumb above.
          "[&::-moz-range-thumb]:h-[20px]",
          "[&::-moz-range-thumb]:w-[20px]",
          "[&::-moz-range-thumb]:rounded-full",
          "[&::-moz-range-thumb]:border-0",
          "[&::-moz-range-thumb]:bg-white",
          "[&::-moz-range-thumb]:shadow-btn-secondary",
          className,
        )}
        {...props}
      />
    );
  },
);
Slider.displayName = "Slider";

export { Slider };
export type { SliderProps };
